import React, { lazy, Suspense } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { GDPRCookieBanner } from "@/components/GDPRCookieBanner";
import { AnalyticsProvider } from "@/components/analytics/AnalyticsProvider";
import { useScrollToTop } from "./hooks/useScrollToTop";
import { AppResourcePreloader } from "@/components/ResourcePreloader";
import { SimpleCriticalCSS } from "@/components/SimpleCriticalCSS";
import { CenteredSpinner } from "@/components/CenteredSpinner";
import { CacheLogger } from "@/components/CacheLogger";
import { useFontLoading, useFontDisplay } from "./hooks/useFontLoading";
import NotFound from "@/pages/not-found";
import { Landing, NewsLayout, StatisticViewer, StatisticsList } from "@/components/LazyComponents";
import BlogList from "@/pages/BlogList";
import BlogView from "@/pages/BlogView";
import BlogNew from "@/pages/BlogNew";
import Home from "@/pages/Home";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminStatistics from "@/pages/AdminStatistics";
import Profile from "@/pages/Profile";
import Legal from "@/pages/Legal";
import { Login } from "@/pages/Login";
import { VerifyEmail } from "@/pages/VerifyEmail";
import Statistics from "@/pages/Statistics";
import StatisticView from "@/pages/StatisticView";
import Favorites from "@/pages/Favorites";
import Authentemia from "@/pages/Authentemia";
import Analytics from "@/pages/Analytics";
import ExperiencesAdmin from "@/pages/ExperiencesAdmin";
import AdminAuthors from "@/pages/AdminAuthors";
import AuthorProfile from "@/pages/AuthorProfile";
import { Loader2 } from "lucide-react";
import "./lib/i18n";
import { getNoIndexRouteMeta } from "@shared/noindex-routes";

/** Stripe pages: lazy so loadStripe never runs on home / other routes. */
const LazySubscribe = lazy(() => import("@/pages/Subscribe"));
const LazyTestStripe = lazy(() => import("@/pages/TestStripe"));

function StripeRouteFallback() {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function Subscribe() {
  return (
    <Suspense fallback={<StripeRouteFallback />}>
      <LazySubscribe />
    </Suspense>
  );
}

function TestStripe() {
  return (
    <Suspense fallback={<StripeRouteFallback />}>
      <LazyTestStripe />
    </Suspense>
  );
}

/** Apply noindex title/robots on private/auth routes (SSR already injects for crawlers). */
function RouteSeoGuard() {
  const [location] = useLocation();
  const noIndex = getNoIndexRouteMeta(location);

  React.useEffect(() => {
    if (!noIndex) return;

    document.title = noIndex.title;
    for (const name of ['robots', 'googlebot'] as const) {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = 'noindex, nofollow';
    }
  }, [location, noIndex]);

  return null;
}

function Router() {
  // Scroll to top on route changes
  useScrollToTop();
  const { isAuthenticated, isLoading } = useAuth();
  
  // Log navigation changes
  const [location] = useLocation();
  React.useEffect(() => {
    // console.log('🧭 NAVIGATION:', {
    //   path: location,
    //   timestamp: new Date().toLocaleTimeString(),
    //   referrer: document.referrer || 'Direct'
    // }); // Removed for production
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col">
      <RouteSeoGuard />
      <Header />
      <main className="flex-1 pt-16">
        <Switch>
          {/* Main route - News Layout as main page */}
          <Route path="/" component={NewsLayout} />

          {/* Legacy Landing page route */}
          <Route path="/landing" component={Landing} />

          {/* Public routes - available to everyone */}
          {/* Public legal pages (canonical Spanish URLs) */}
          <Route path="/sobre-statemia" component={Legal} />
          <Route path="/contacto" component={Legal} />
          <Route path="/aviso-legal" component={Legal} />
          <Route path="/politica-de-privacidad" component={Legal} />
          <Route path="/politica-de-cookies" component={Legal} />
          <Route path="/terminos-de-servicio" component={Legal} />
          {/* Legacy legal routes → client redirect to canonical */}
          <Route path="/legal/:section" component={Legal} />
          <Route path="/legal" component={Legal} />
          <Route path="/login" component={Login} />
          <Route path="/verify-email" component={VerifyEmail} />
          <Route path="/autor/:slug" component={AuthorProfile} />
          <Route path="/statistics-list" component={StatisticsList} />
          <Route path="/statistics/:slug" component={StatisticViewer} />
          {/* Blog */}
          <Route path="/blog" component={BlogList} />
          <Route path="/blog/:slug" component={BlogView} />
          <Route path="/admin/blog/new" component={BlogNew} />
          
          {/* Admin experiences - component has its own auth check */}
          <Route path="/admin/experiences" component={ExperiencesAdmin} />
          <Route path="/admin/authors" component={AdminAuthors} />

          {/* Protected routes - only for authenticated users */}
          {isAuthenticated && (
            <>
              <Route path="/home" component={Home} />
              <Route path="/dashboard" component={Dashboard} />
              <Route path="/admin" component={AdminDashboard} />
              <Route path="/admin/statistics" component={AdminStatistics} />
              <Route path="/statistics" component={Statistics} />
              <Route path="/statistics/edit/:id" component={StatisticView} />
              <Route path="/favorites" component={Favorites} />
              <Route path="/authentemia" component={Authentemia} />
              <Route path="/subscribe" component={Subscribe} />
              <Route path="/profile" component={Profile} />
              <Route path="/analytics" component={Analytics} />
              {/* Dev/admin only — not public */}
              <Route path="/test-stripe" component={TestStripe} />
            </>
          )}

          {/* Fallback to 404 */}
          <Route component={NotFound} />
        </Switch>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  // Initialize font loading
  useFontDisplay();
  const { isLoaded: fontsLoaded } = useFontLoading();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="saas-theme">
        <TooltipProvider>
          <AuthProvider>
            <AnalyticsProvider>
              <AppResourcePreloader />
              <SimpleCriticalCSS />
              {process.env.NODE_ENV === 'development' && <CacheLogger />}
              <CenteredSpinner isLoading={!fontsLoaded}>
                <Toaster />
                <Router />
              </CenteredSpinner>
              <GDPRCookieBanner />
            </AnalyticsProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
